import * as React from 'react';

import { EntityActions, EntityType } from 'common/entity';

import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { InfiniteList } from 'common/components/InfiniteList';
import { Loading } from 'common/components/Loading';
import { Table } from 'reactstrap';
import { toApiError } from 'common/utils/api';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Member } from 'common/api/models/Member';
import { Datetime } from 'common/components/Datetime';
import { Analytics } from 'common/utils/Analytics';
import { LinkedinLink } from 'common/components/link/LinkedinLink';
import { formatStaffCountRange } from 'common/utils/format';

interface OrgsTableProps {
  appState: AppState;
  entityActions: EntityActions;
  search: string;
  onClick: (id: string) => void;
}
interface OrgsTableState {
  orgs: Member[];
  next?: string;
  isLoading: boolean;
  error?: string;
}
export class OrgsTable extends React.Component<OrgsTableProps, OrgsTableState> {
  state: OrgsTableState = { orgs: [], isLoading: true };

  handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    this.props.onClick(e.currentTarget.dataset.number!);
  };

  handleLoadMore = () => {
    this.reload(this.props.search, this.state.next);
  };

  componentDidMount() {
    this.reload(this.props.search);
  }

  componentDidUpdate(prevProps: OrgsTableProps) {
    if (prevProps.search !== this.props.search) {
      this.reload(this.props.search);
    }
  }

  reload(searchQuery: string, from?: string) {
    this.setState({ isLoading: true });
    const args = { q: searchQuery + ' type:org', from };
    Analytics.member.search(args);
    MemberApi.findMembers(args).then(
      results => {
        this.setState({
          orgs: from ? this.state.orgs.concat(results.data!) : results.data!,
          next: results.next,
          error: undefined,
          isLoading: false,
        });
        const parentOrgIds = results.data.filter(o => !!o.parentOrgId).map(o => o.parentOrgId!);
        this.props.entityActions.fetchMissingIds(EntityType.MEMBER, parentOrgIds);
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  }

  render() {
    const cols = 8;
    const entities = this.props.appState.entities;
    return (
      <InfiniteList hasMore={!!this.state.next} isLoading={this.state.isLoading} onLoadMore={this.handleLoadMore}>
        {this.state.error && (
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {this.state.error}
          </div>
        )}

        <Table className="table-clickable" responsive={true} hover={true} striped={true}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Parent</th>
              <th>Status</th>
              <th>Type</th>
              <th>Chapter</th>
              <th>CEU Provider</th>
              <th>Expire</th>
              <th>Count</th>
              <th>LinkedIn Page</th>
              <th>Staff Count Range</th>
              <th>Signup Source</th>
              <th>Account Manager</th>
              <th>NAICS Code</th>
              <th>SIC Code</th>
              <th>D&B Revenue</th>
              <th>Global Connect</th>
            </tr>
          </thead>
          <tbody>
            {this.state.orgs.length === 0 &&
              !this.state.isLoading && (
                <tr>
                  <td colSpan={cols}>No results found.</td>
                </tr>
              )}
            {this.state.orgs.map(o => (
              <tr key={o.id} data-number={o.number} onClick={this.handleRowClick}>
                <td>{o.name}</td>
                <td>
                  {o.parentOrgId &&
                    entities.member[o.parentOrgId] && <span>{entities.member[o.parentOrgId].name}</span>}
                </td>
                <td>
                  {o.status === Member.Status.ACTIVE && <Badge type="success" text="Active" />}{' '}
                  {o.status === Member.Status.EXPIRED && <Badge type="danger" text="Expired" />}{' '}
                  {o.status === Member.Status.INACTIVE && <Badge type="danger" text="Inactive" />}{' '}
                </td>
                <td>{o.memberType}</td>
                <td>
                  {o.chapterId && entities.chapter[o.chapterId] && entities.chapter[o.chapterId].name}
                  {!o.chapterId && 'None'}
                </td>
                <td>{o.vars.ceuProvider && <Badge type="warning" text="CEU Provider" />}</td>
                <td>
                  <Datetime at={o.expireDate} format="M/D/YY" />
                </td>
                <td style={{ textAlign: 'right' }}>
                  {o.branchCount! > 0 && (
                    <span title="Branches">
                      <i className="fa fa-building" /> {o.branchCount}
                    </span>
                  )}{' '}
                </td>
                <td>
                  <LinkedinLink member={o} />
                </td>
                <td>{formatStaffCountRange(o.staffCountRange)}</td>
                <td>
                  {o.signupSource === Member.SignupSource.ONLINE
                    ? 'Online'
                    : o.signupSource && o.signupSource === Member.SignupSource.CHAPTER
                      ? 'Chapter'
                      : o.signupSource && o.signupSource === Member.SignupSource.SALES
                        ? 'Sales'
                        : o.signupSource && o.signupSource === Member.SignupSource.REACTIVATION
                          ? 'Reactivation'
                          : o.signupSource && o.signupSource === Member.SignupSource.EVENT
                            ? 'Event'
                            : o.signupSource && 'None'}
                  {!o.signupSource && 'None'}
                </td>
                <td>{o.accountManager}</td>
                <td>{o.naicsCode}</td>
                <td>{o.sicCode}</td>
                <td>{o.dnbRevenue}</td>
                <td>{o.isGlobalConnect ? 'Yes' : 'No'}</td>
              </tr>
            ))}
            {this.state.isLoading && (
              <tr>
                <td colSpan={cols}>
                  <Loading />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </InfiniteList>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/OrgsTable.tsx